import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { GetGatedAccessEventResponse } from '../types';
import { escapeCsvValue } from '../helpers/files';
import format from 'date-fns/format';
import { getGatedAccessData } from '../helpers/utils';
import topicData from '../helpers/state/topic-data';
import { getMainPage } from '../helpers/page-name';

@customElement('gated-access-insights-component')
export class GatedAccessInsightsComponent extends LitElement {
    amplitude_callsite: string = 'Gated Access Insights';

    @property({type: Array})
    accesses: GetGatedAccessEventResponse[] = [];

    @property({type: Array})
    paywallAccesses: GetGatedAccessEventResponse[] = [];

    @property({type: Array})
    passwordAccesses: GetGatedAccessEventResponse[] = [];

    @property({type: Array})
    emailAccesses: GetGatedAccessEventResponse[] = [];

    @state()
    selectedTab: string = 'password';

    @state()
    totalEarned: number = 0;

    async firstUpdated() {
        try {
            await this.getAccesses();
        } catch (error) {
            getMainPage().errorToastElement?.showToastAndThrow('Error getting accesses.' + (error as Error).message);
        }
    }

    private async getAccesses() {
        const accessData = await getGatedAccessData(this.amplitude_callsite, topicData.topicId, topicData.topicKey);
        this.accesses = accessData.accesses;
        this.paywallAccesses = accessData.paywall_accesses;
        this.passwordAccesses = accessData.password_accesses;
        this.emailAccesses = accessData.email_accesses;
        this.totalEarned = accessData.total_earned;
    }

    private handleChangeTab(tab: string) {
        this.selectedTab = tab;
    }

    private handleExportData() {
        let content = '';

        content += 'Id,Type,Email,GateName,IpAddress,"Timestamp",Value,"Percent Earned","Invoice"\n';

        for (const accesses of this.accesses) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            content += `${escapeCsvValue(accesses._id.$oid ?? '')},${escapeCsvValue(accesses.gate_type)},${escapeCsvValue(accesses.email ?? '')},${escapeCsvValue(accesses.gate_name ?? '')},${escapeCsvValue(accesses.ip_address ?? '')},${escapeCsvValue(format(new Date(accesses.timestamp), 'MMM dd yyyy @ h:mm a'))},${escapeCsvValue(String(accesses.transaction_amount ?? 0))},${escapeCsvValue(String(accesses.transaction_percent_to_creator ?? 0))}, ${escapeCsvValue(accesses.transaction_id ?? '')}\n`;
        }

        const filename = 'accesses.csv';

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8'
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);

        document.body.removeChild(a);
    }

    private handleClickBack() {
        this.dispatchEvent(
            new CustomEvent('on-back')
        );
    }


    render() {
        return html`
            <div class="addon__container">
                <div class="back-button" @click="${this.handleClickBack}">
                    <mwc-icon class="back-button__icon">arrow_back</mwc-icon>
                    <span>Back to Gated Access </span>
                </div>

                <div class="main-wrapper">
                    <h6 class="title">
                        Gated Access Insights
                    </h6>

                    <div class="gated-access__tabs">
                        <div
                            class="gated-access__tab left ${this.selectedTab === 'password'
                                ? 'active'
                                : ''}"
                            @click="${() => this.handleChangeTab('password')}"
                        >
                            <span>Passwords</span>
                        </div>
                        <div
                            class="gated-access__tab middle ${this.selectedTab ===
                            'paywall'
                                ? 'active'
                                : ''}"
                            @click="${() => this.handleChangeTab('paywall')}"
                        >
                            <span>Paywalls</span>
                        </div>
                        <div
                            class="gated-access__tab right ${this.selectedTab ===
                            'email'
                                ? 'active'
                                : ''}"
                            @click="${() => this.handleChangeTab('email')}"
                        >
                            <span>Email Collection</span>
                        </div>

                    </div>

                    ${this.selectedTab === 'password' ? html`
                        <div class="details-info__wrapper">
                        Total Accesses: ${this.passwordAccesses.length}
                        <div class="item-list">
                                <div class="item-list__row">
                                    <div class="item-row__status">
                                        <span class="item-row__header">
                                            Password Name
                                        </span>
                                    </div>
                                    <div class="item-row__name">
                                        <span class="item-row__header">
                                            Email or IP
                                        </span>
                                    </div>
                                    <div class="item-row__edit">
                                        <span class="item-row__header">
                                            Timestamp
                                        </span>
                                    </div>
                                </div>
                                ${this.passwordAccesses.map((access: GetGatedAccessEventResponse) => {
                                    return html`
                                        <div class="item-list__row">
                                            <div class="item-row__status">
                                                <span class="item-row__label">
                                                    ${access.gate_name ?? ''}
                                                </span>
                                            </div>
                                            <div class="item-row__name">
                                                <span class="item-row__label">
                                                    ${access.email ?? access.ip_address ?? ''}
                                                </span>
                                            </div>
                                            <div class="item-row__edit">
                                                <span class="item-row__label">
                                                    ${format(new Date(access.timestamp), 'MMM dd, yyyy @ h:mm a') ?? ''}
                                                </span>
                                            </div>
                                        </div>
                                    `;
                                })}

                            </div>
                        </div>`
                    : this.selectedTab === 'paywall' ? html`
                        <div class="details-info__wrapper">
                        Total Earned: ${this.totalEarned}
                        <div class="item-list">
                                <div class="item-list__row">
                                    <div class="item-row__status">
                                        <span class="item-row__header">
                                            Gate Name & Price
                                        </span>
                                    </div>
                                    <div class="item-row__name">
                                        <span class="item-row__header">
                                            Email or IP
                                        </span>
                                    </div>
                                    <div class="item-row__edit">
                                        <span class="item-row__header">
                                            Timestamp
                                        </span>
                                    </div>
                                </div>
                                ${this.paywallAccesses.map((access: GetGatedAccessEventResponse) => {
                                    return html`
                                        <div class="item-list__row">
                                            <div class="item-row__status">
                                                <span class="item-row__label">
                                                    ${access.gate_name ?? ''}: ${access.transaction_amount ?? 0}
                                                </span>
                                            </div>
                                            <div class="item-row__name">
                                                <span class="item-row__label">
                                                    ${access.email ?? access.ip_address ?? ''}
                                                </span>
                                            </div>
                                            <div class="item-row__edit">
                                                <span class="item-row__label">
                                                    ${format(new Date(access.timestamp), 'MMM dd, yyyy @ h:mm a') ?? ''}
                                                </span>
                                            </div>
                                        </div>
                                    `;
                                })}

                            </div>
                        </div>`
                    : html`
                        <div class="details-info__wrapper">
                        Total Email Accesses: ${this.emailAccesses.length}
                        <div class="item-list">
                                <div class="item-list__row">
                                    <div class="item-row__status">
                                        <span class="item-row__header">
                                            Email
                                        </span>
                                    </div>
                                    <div class="item-row__name">
                                        <span class="item-row__header">
                                            IP Address
                                        </span>
                                    </div>
                                    <div class="item-row__edit">
                                        <span class="item-row__header">
                                            Timestamp
                                        </span>
                                    </div>
                                </div>
                                ${this.emailAccesses.map((access: GetGatedAccessEventResponse) => {
                                    return html`
                                        <div class="item-list__row">
                                            <div class="item-row__status">
                                                <span class="item-row__label">
                                                    ${access.email ?? ''}
                                                </span>
                                            </div>
                                            <div class="item-row__name">
                                                <span class="item-row__label">
                                                    ${access.ip_address ?? ''}
                                                </span>
                                            </div>
                                            <div class="item-row__edit">
                                                <span class="item-row__label">
                                                    ${format(new Date(access.timestamp), 'MMM dd, yyyy @ h:mm a') ?? ''}
                                                </span>
                                            </div>
                                        </div>
                                    `;
                                })}
                        </div>`
                    }
                    <div class="buttons-wrapper">
                        <mwc-button
                            raised
                            class=""
                            @click="${this.handleExportData}"
                        >
                            <mwc-icon class="back-button__icon">file_download</mwc-icon>
                            <span>Export Data</span>
                        </mwc-button>
                    </div>
                </div>
            </div>
        `;
    }

    static get styles() {
        return css`
            .addon__container {
                max-height: calc(100vh - 162px);
                overflow: hidden auto;
                margin-bottom: 1rem;
                padding-right: 30px;
                box-sizing: border-box;
            }

            .main-wrapper {
                margin-top: 24px;
                display: flex;
                width: 100%;
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
            }

            .gated-access__tabs {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .gated-access__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
                color: #091E2C;
                text-align: center;
                font-family: Roboto;
                font-weight: 500;
                line-height: 140%;
                letter-spacing: 0.1px;
            }

            .gated-access__tab.active {
                background: #102C3D;
                color: #F9EFEC;
            }

            .title {
                color: #000;
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
            }

            .description {
                color: #091E2C;
                font-size: 16px;
                line-height: 150%;
                margin: 0;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .back-button {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 6px;
                cursor: pointer;
            }

            .back-button__icon {
                --mdc-icon-size: 24px;
            }

            .back-button__label {
                color: #000;
                text-align: center;
                font-size: 16px;
                line-height: 150%;
            }

            .details-info__wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 16px;
            }

            .deatils-row__header,
            .deatils-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 16px;
            }

            .details__label,
            .details__info {
                flex: 1;
                text-align: left;
            }

            .details__label {
                color: #443F56;
                font-size: 16px;
                font-weight: 700;
                line-height: 150%;
            }

            .details__info {
                color: #091E2C;
                font-size: 16px;
                line-height: 150%;
            }

            h6.details__label {
                color: #091E2C;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
            }

            p.details__info {
                color: #091E2C;
                font-size: 16px;
                line-height: 150%;
                margin: 0;
            }

            h5.details__header {
                color: #091E2C;
                text-align: center;
                font-size: 20px;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
            }

            .buttons-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                margin: 24px 0;
            }

            .item-list {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            }

            .item-list__row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 10px 0;
                gap: 14px;
                border-bottom: 1px solid #666276;
            }

            .item-row__status {
                flex: 1;
                text-align: center;
            }

            .item-row__name {
                flex: 3;
                text-align: center;
            }

            .item-row__copy {
                flex: 2;
                text-align: center;
            }

            .item-row__edit {
                flex: 2;
                text-align: center;
            }

            .item-row__uses {
                flex: 2;
                text-align: center;
            }

            .item-row__header {
                color: #443F56;
                text-align: center;
                font-weight: 500;
            }

            .item-row__action {
                --mdc-icon-size: 24px;
                cursor: pointer;
            }

            .action-buttons {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 1rem;
            }

            .item-add-button {
                width: 100%;
                display: flex;
                justify-content: left;
                align-items: flex-start;
                gap: 4px;
            }

            .item-add__icon {
                --mdc-icon-size: 20px;
            }

            .item-add__label {
                color: #666276;
                text-decoration: underline;
                cursor: pointer;
            }
        `;
    }
}